<template>
    <div class="mt-2 rounded-2xl border border-app bg-card/70 backdrop-blur-2xl shadow-lg overflow-hidden">
        <!-- Step indicator -->
        <div class="flex items-center justify-between gap-2 px-6 py-4 border-b border-app bg-muted/5">
            <div v-for="s in steps" :key="s.step" class="flex items-center gap-2">
                <span class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-xs font-bold" :class="[
                    wizardStep === s.step ? 'border-brand-500 text-brand-500' :
                    wizardStep > s.step ? 'border-brand-500 bg-brand-500 text-on-brand' : 'border-app text-body/80'
                ]">
                    <Check v-if="wizardStep > s.step" class="h-3.5 w-3.5" />
                    <template v-else>{{ s.step }}</template>
                </span>
                <span class="text-sm font-bold" :class="wizardStep === s.step ? 'text-brand-500' : 'text-body/80'">
                    {{ s.label }}
                </span>
            </div>
        </div>

        <div class="p-6">
            <!-- Step 1: Details -->
            <div v-if="wizardStep === 1" class="space-y-6">
                <div class="inline-flex w-full items-center rounded-2xl border border-app bg-muted/5 p-1">
                    <button type="button" @click="registrationType = 'individual'"
                        class="px-5 py-3 flex-1 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2"
                        :class="registrationType === 'individual' ? 'bg-brand-500 text-on-brand shadow' : 'text-body/80'">
                        <User class="h-4 w-4" /> Just me
                    </button>
                    <button type="button" @click="registrationType = 'group'"
                        class="px-5 py-3 flex-1 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2"
                        :class="registrationType === 'group' ? 'bg-brand-500 text-on-brand shadow' : 'text-body/80'">
                        <Users class="h-4 w-4" /> Group registration
                    </button>
                </div>

                <!-- Just me -->
                <template v-if="registrationType === 'individual'">
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <label class="block col-span-full">
                            <span class="text-xs font-bold text-body/80 uppercase tracking-wider">Full name</span>
                            <input v-model.trim="innerForm.fullName" type="text" autocomplete="name" :class="fieldClass" />
                        </label>
                        <label class="block">
                            <span class="text-xs font-bold text-body/80 uppercase tracking-wider">Phone number</span>
                            <PhoneInput v-model.trim="innerForm.phone" type="tel" autocomplete="tel" :class="fieldClass" />
                        </label>
                        <label class="block">
                            <span class="text-xs font-bold text-body/80 uppercase tracking-wider">Email</span>
                            <input v-model.trim="innerForm.email" type="email" autocomplete="email" :class="fieldClass" />
                        </label>
                    </div>


                    <div>
                        <span class="text-xs font-bold text-body/80 uppercase tracking-wider">Race category</span>
                        <p v-if="!rows.length" class="text-body/80 text-sm mt-1">No categories available.</p>
                        <div class="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <button v-for="row in rows" :key="ticketKey(row.ticket)" type="button"
                                :disabled="isSoldOut(row.ticket)" @click="selectedKey = ticketKey(row.ticket)"
                                class="text-center rounded-2xl border p-4 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                :class="selectedKey === ticketKey(row.ticket) ? 'border-brand-500 ring-1 ring-brand-500/30 bg-brand-500/5' : 'border-app hover:bg-muted/10'">
                                <p class="text-header font-black text-lg">{{ row.ticket.title }}</p>
                                <p class="text-brand-500 text-xs font-bold mt-1">{{ priceLabel(row.ticket) }}</p>
                                <p v-if="row.ticket.description" class="text-body/80 text-xs mt-1 line-clamp-2">{{ row.ticket.description }}</p>
                                <p v-if="isSoldOut(row.ticket)" class="text-body/80 text-xs font-bold uppercase mt-2">Sold out</p>
                            </button>
                        </div>
                    </div>


                    <div>
                        <span class="text-xs font-bold text-body/80 uppercase tracking-wider">T-shirt size <span class="normal-case font-medium">(optional)</span></span>
                        <div class="mt-2 flex flex-wrap gap-2">
                            <button v-for="size in tshirtSizes" :key="size" type="button" @click="innerForm.tshirtSize = innerForm.tshirtSize === size ? '' : size"
                                class="h-10 px-3 rounded-xl border text-sm font-bold transition-all"
                                :class="innerForm.tshirtSize === size ? 'border-brand-500 bg-brand-500/10 text-brand-500' : 'border-app text-header hover:bg-muted/10'">
                                {{ size }}
                            </button>
                        </div>
                    </div>
                </template>

                <!-- Group registration -->
                <template v-else>
                    <p class="text-body/80 text-sm leading-relaxed">
                        Register two or more people together and pay once. Everyone you name gets their own ticket,
                        and rows you leave empty are ignored.
                    </p>

                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <label class="block col-span-full">
                            <span class="text-xs font-bold text-body/80 uppercase tracking-wider">Your name (group leader)</span>
                            <input v-model.trim="innerForm.fullName" type="text" autocomplete="name" :class="fieldClass" />
                        </label>
                        <label class="block">
                            <span class="text-xs font-bold text-body/80 uppercase tracking-wider">Phone number</span>
                            <PhoneInput v-model.trim="innerForm.phone" type="tel" autocomplete="tel" :class="fieldClass" />
                        </label>
                        <label class="block">
                            <span class="text-xs font-bold text-body/80 uppercase tracking-wider">Email</span>
                            <input v-model.trim="innerForm.email" type="email" autocomplete="email" :class="fieldClass" />
                        </label>
                    </div>

                    <div>
                        <span class="text-xs font-bold text-body/80 uppercase tracking-wider">Runners</span>
                        <div class="mt-2 space-y-2">
                            <div v-for="(r, i) in runners" :key="i"
                                class="grid grid-cols-1 sm:grid-cols-[1.5rem_1fr_4.5rem_7.5rem_7.5rem_auto] items-center gap-2">
                                <span class="hidden sm:block text-xs font-bold text-body/80">{{ i + 1 }}</span>
                                <input v-model.trim="r.fullName" type="text" placeholder="Full name" :class="rowFieldClass" />
                                <input v-model="r.age" type="number" min="0" placeholder="Age" :class="rowFieldClass" />
                                <select v-model="r.tshirtSize" :class="rowFieldClass">
                                    <option value="">T-shirt size</option>
                                    <option v-for="size in tshirtSizes" :key="size" :value="size">{{ size }}</option>
                                </select>
                                <select v-model="r.ticketKey" :class="rowFieldClass">
                                    <option value="">Distance</option>
                                    <option v-for="row in rows" :key="ticketKey(row.ticket)" :value="ticketKey(row.ticket)"
                                        :disabled="isSoldOut(row.ticket)">
                                        {{ row.ticket.title }}
                                    </option>
                                </select>
                                <button type="button" @click="removeRunner(i)" :disabled="runners.length <= 1"
                                    :aria-label="`Remove runner ${i + 1}`"
                                    class="justify-self-end h-8 w-8 rounded-lg border border-app text-body/80 flex items-center justify-center hover:bg-muted/10 transition-colors disabled:opacity-40">
                                    <X class="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                        <button type="button" @click="addRunner" :disabled="runners.length >= maxQty"
                            class="mt-2 text-sm font-bold text-brand-500 flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed">
                            <Plus class="h-3.5 w-3.5" /> Add another runner
                        </button>
                        <p v-if="runners.length >= maxQty" class="text-body/80 text-xs mt-1">
                            Up to {{ maxQty }} people per registration.
                        </p>
                    </div>

                    <div class="rounded-xl bg-muted/10 p-3 text-sm text-header font-semibold">
                        <template v-if="namedRunners.length">
                            {{ namedRunners.length }} runner{{ namedRunners.length > 1 ? 's' : '' }} · Total {{ formatMoney(groupTotal, currency) }}
                            <span v-if="runnersMissingDistance" class="block text-red-500 font-medium mt-1">
                                Pick a distance for every named runner.
                            </span>
                        </template>
                        <template v-else>Add your runners' names to see the total.</template>
                    </div>
                </template>

                <button type="button" :disabled="!canContinue" @click="continueToPayment"
                    class="w-full px-5 py-3.5 rounded-2xl bg-brand-500 text-on-brand font-bold hover:bg-brand-600 transition-all active:scale-95 disabled:opacity-40">
                    Continue to Payment
                </button>
            </div>

            <!-- Step 2: Payment -->
            <div v-else-if="wizardStep === 2" class="space-y-6">
                <PaymentPanel v-if="step === 'payment'" :order-id="orderId" :amount="form.amount" :currency="currency"
                    :contact-phone="form.phone" @paid="emit('paid')" />

                <template v-else>
                    <div class="rounded-2xl bg-card border border-app divide-y divide-app text-sm">
                        <div v-for="row in bookedRows" :key="ticketKey(row.ticket)" class="flex justify-between gap-3 p-4">
                            <span class="text-header font-semibold">{{ row.ticket.title }} × {{ row.quantity }}</span>
                            <span class="text-header font-semibold">{{ formatMoney(row.ticket.price * row.quantity, currency) }}</span>
                        </div>
                    </div>

                    <div class="flex items-center justify-between border-t border-app pt-4">
                        <span class="text-sm font-bold text-header uppercase tracking-wider">Total</span>
                        <span class="text-2xl font-black text-header">{{ formatMoney(form.amount, currency) }}</span>
                    </div>

                    <p v-if="error" class="text-sm text-red-500 font-semibold">{{ error }}</p>

                    <div class="flex items-center gap-3">
                        <button type="button" @click="wizardStep = 1"
                            class="shrink-0 h-12 w-12 rounded-2xl border border-app text-header flex items-center justify-center hover:bg-muted/10 transition-colors">
                            <ChevronLeft class="h-5 w-5" />
                        </button>
                        <button type="button" :disabled="step === 'processing'" @click="emit('submit')"
                            class="flex-1 px-5 py-3.5 rounded-2xl bg-brand-500 text-on-brand font-bold hover:bg-brand-600 transition-all active:scale-95 disabled:opacity-60 flex items-center justify-center gap-2">
                            <span v-if="step === 'processing'" class="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin"></span>
                            {{ step === 'processing' ? 'Processing…' : (form.amount > 0 ? `Pay ${formatMoney(form.amount, currency)}` : 'Reserve free tickets') }}
                        </button>
                    </div>
                    <p class="text-center text-[11px] text-body/80">By registering you agree to the event terms and code of conduct.</p>
                </template>
            </div>

            <!-- Step 3: Ticket -->
            <div v-else class="text-center py-6">
                <div class="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-500/10 text-brand-500">
                    <CheckCircle2 class="h-9 w-9" />
                </div>
                <h4 class="text-xl font-bold text-header mb-2">Registration confirmed</h4>
                <p class="text-body/80 text-sm leading-relaxed mb-6">
                    We've sent a confirmation to <span class="text-header font-semibold">{{ form.email }}</span>.
                    Your reference is <span class="text-header font-mono font-semibold">{{ reference }}</span>.
                </p>
                <button type="button" @click="restart"
                    class="px-6 py-3 rounded-2xl bg-brand-500 text-on-brand font-bold hover:bg-brand-600 transition-all active:scale-95">
                    Register another
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch } from 'vue';
import { Check, CheckCircle2, ChevronLeft, Plus, User, Users, X } from 'lucide-vue-next';
import { EventTicketType, Order } from '~/types';
import { formatMoney, parseFullName } from '../helpers';
import PhoneInput from '@core/forms/PhoneInput.vue';
import PaymentPanel from './PaymentPanel.vue';

const props = defineProps<{
    rows: { ticket: EventTicketType; quantity: number }[];
    maxQty: number;
    form: Order;
    currency: string;
    step: 'form' | 'processing' | 'payment' | 'done';
    error: string;
    reference: string;
    orderId: string;
}>();

const emit = defineEmits<{
    (e: 'update:qty', ticket: EventTicketType, quantity: number): void;
    (e: 'update:holder', key: string, index: number, value: any): void;
    (e: 'submit'): void;
    (e: 'reset'): void;
    (e: 'paid'): void;
}>();

const steps = [
    { step: 1, label: 'Details' },
    { step: 2, label: 'Payment' },
    { step: 3, label: 'Ticket' },
];

const fieldClass =
    'mt-1.5 w-full form-control transition-all';

const rowFieldClass =
    'w-full rounded-xl border border-app bg-card/70 px-3 py-2 text-sm text-header outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-all';

const tshirtSizes = ['2XS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'];

const ticketKey = (t: EventTicketType) => t.id || t.title;
const isSoldOut = (t: EventTicketType) => t.soldOut === true || t.available === false;
const priceLabel = (t: EventTicketType) => formatMoney(t.price, t.currency);
const priceFor = (key: string) => props.rows.find((r) => ticketKey(r.ticket) === key)?.ticket.price ?? 0;

const wizardStep = ref<1 | 2 | 3>(1);
const registrationType = ref<'individual' | 'group'>('individual');
const selectedKey = ref<string | null>(null);
const innerForm = reactive({
    fullName: '',
    phone: '',
    email: '',
    age: '',
    tshirtSize: '',
});

// One row per person for a group registration; empty rows are ignored on submit.
type Runner = { fullName: string; age: string; tshirtSize: string; ticketKey: string };
const emptyRunner = (): Runner => ({ fullName: '', age: '', tshirtSize: '', ticketKey: '' });
const runners = ref<Runner[]>([emptyRunner(), emptyRunner()]);
const addRunner = () => runners.value.push(emptyRunner());
const removeRunner = (i: number) => {
    if (runners.value.length > 1) runners.value.splice(i, 1);
};

const namedRunners = computed(() => runners.value.filter((r) => r.fullName.trim()));
const runnersMissingDistance = computed(() => namedRunners.value.some((r) => !r.ticketKey));
const groupTotal = computed(() =>
    namedRunners.value.reduce((sum, r) => sum + (r.ticketKey ? priceFor(r.ticketKey) : 0), 0)
);

// Rows with a quantity attached, for the Step 2 order summary (works for both modes).
const bookedRows = computed(() => props.rows.filter((r) => r.quantity > 0));

const canContinue = computed(() =>
    registrationType.value === 'group'
        ? !!innerForm.fullName && !!innerForm.email && namedRunners.value.length > 0 && !runnersMissingDistance.value
        : !!selectedKey.value && !!innerForm.fullName && !!innerForm.email
);

const splitName = (name: string) => {
    return parseFullName(name);
};

const updateTicketData = () => {
    props.form.firstName = splitName(innerForm.fullName).firstName;
    props.form.secondName = splitName(innerForm.fullName).secondName;
    props.form.lastName = splitName(innerForm.fullName).lastName;
    props.form.email = innerForm.email;
    props.form.phone = innerForm.phone;

    if (registrationType.value === 'group') {
        // Group runners by the ticket type (distance) they each picked.
        const byTicket = new Map<string, Runner[]>();
        for (const r of namedRunners.value) {
            const list = byTicket.get(r.ticketKey) ?? [];
            list.push(r);
            byTicket.set(r.ticketKey, list);
        }

        for (const row of props.rows) {
            const key = ticketKey(row.ticket);
            emit('update:qty', row.ticket, (byTicket.get(key) ?? []).length);
        }
        for (const [key, list] of byTicket) {
            list.forEach((r, i) => {
                emit('update:holder', key, i, { ...splitName(r.fullName), email: '', phone: '', age: r.age || undefined, tshirtSize: r.tshirtSize || undefined });
            });
        }
    } else if (selectedKey.value) {
        for (const row of props.rows) {
            const key = ticketKey(row.ticket);
            emit('update:qty', row.ticket, key === selectedKey.value ? 1 : 0);
        }
        emit('update:holder', selectedKey.value, 0, {
            ...splitName(innerForm.fullName),
            email: innerForm.email,
            phone: innerForm.phone,
            age: innerForm.age || undefined,
            tshirtSize: innerForm.tshirtSize || undefined,
        });
    }

}

const continueToPayment = () => {
    if (!canContinue.value) return;
    updateTicketData()
    
    wizardStep.value = 2;
};

// Advance to the confirmation step once the parent reports the order is done.
watch(
    () => props.step,
    (s) => {
        if (s === 'done') wizardStep.value = 3;
    }
);

watch([runners, innerForm, selectedKey], ([],[])=>{
    updateTicketData()
}, { deep: true})

const restart = () => {
    emit('reset');
    wizardStep.value = 1;
    registrationType.value = 'individual';
    selectedKey.value = null;
    innerForm.fullName = '';
    innerForm.phone = '';
    innerForm.email = '';
    innerForm.age = '';
    innerForm.tshirtSize = '';
    runners.value = [emptyRunner(), emptyRunner()];
};
</script>

<style scoped>
.bg-card {
    background-color: rgb(var(--core-card-bg));
}

.border-app {
    border-color: rgb(var(--core-app-border));
}

.divide-app> :not([hidden])~ :not([hidden]) {
    border-color: rgb(var(--core-app-border));
}

.text-header {
    color: rgb(var(--core-header-text));
}

.text-muted {
    color: rgb(var(--core-muted-text));
}
</style>
